import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, MessageCircle, Phone } from 'lucide-react';
import { LocationMap } from './LocationMap';
import { useBooking } from './BookingContext';
import { useLanguage } from './i18n/LanguageContext';
import { PHONE_TEL, WHATSAPP_URL } from './data';
export function Contact() {
  const {
    openBooking
  } = useBooking();
  const {
    t
  } = useLanguage();
  const info = [{
    icon: MapPin,
    label: t.contact.labelLocation,
    value: <>
        96-02 37th Ave
        <br data-id="element-137" data-mp="3ox7ckqg725vl" />
        Corona, NY 11368
      </>
  }, {
    icon: Clock,
    label: t.contact.labelHours,
    value: <>
        {t.contact.hoursValueTop}
        <br data-id="element-138" data-mp="3f6ckh5g9oc28" />
        {t.contact.hoursValueBottom}
      </>
  }, {
    icon: MessageCircle,
    label: t.contact.labelWhatsApp,
    value: <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne" data-id="element-139" data-mp="ggks1ieagj03">
        (347) 888-6047
      </a>,
    highlight: true
  }, {
    icon: Phone,
    label: t.contact.labelCall,
    value: <a href={`tel:${PHONE_TEL}`} className="transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne" data-id="element-140" data-mp="3ouxp9aauwdr5">
        (347) 242-2967
      </a>,
    highlight: true
  }];
  return <section id="contacto" className="max-w-2xl mx-auto px-6 pt-16 scroll-mt-24" data-id="element-141" data-mp="3f3bd9bqp2anc">
      {/* INFO STRIP */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-border mb-9" data-id="element-142" data-mp="313s5wl5pj3c3">
        {info.map(cell => {
        const Icon = cell.icon;
        const isContactChannel = Boolean(cell.highlight);
        return <div key={cell.label} className="bg-warm-white px-4 py-6 text-center flex flex-col items-center" data-id="element-143" data-mp="2s6hwz6gd3qac">
              {isContactChannel ? <span className="metallic-gold-surface mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-metallic-border shadow-[0_3px_9px_rgba(183,144,72,0.24)]" data-id="element-144" data-mp="qdblt37p106t">
                  <Icon className="h-5 w-5 text-white drop-shadow-[0_1px_1px_rgba(73,53,20,0.35)]" strokeWidth={1.7} data-id="element-145" data-mp="23bkgux1zhpb8" />
                </span> : <span className="mb-3 flex h-11 w-11 items-center justify-center" aria-hidden="true" data-id="element-146" data-mp="1yc7su8zdmc72">
                  <Icon className="h-5 w-5 text-champagne-deep" strokeWidth={1.45} data-id="element-147" data-mp="1ua48euuqbx9n" />
                </span>}
              <div className="font-body font-medium text-[10px] tracking-[3px] uppercase text-soft mb-1.5" data-id="element-148" data-mp="1pcuile3fhjla">
                {cell.label}
              </div>
              <div className="font-serif text-dark text-base leading-snug" data-id="element-149" data-mp="2i0gk8gt3ie5i">{cell.value}</div>
            </div>;
      })}
      </div>

      <LocationMap data-id="element-150" data-mp="2d8yivrn5ubk0" />

      {/* CTA */}
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.7
    }} className="relative overflow-hidden border border-metallic-border bg-dark px-7 py-11 text-center shadow-[0_16px_36px_rgba(44,33,24,0.2)] sm:px-12 sm:py-14" data-id="element-151" data-mp="2d7xajuxj435z">
        <span aria-hidden="true" className="pointer-events-none absolute -right-5 -top-16 select-none font-serif text-[260px] leading-none text-champagne/[0.08]" data-id="element-152" data-mp="38bsg420xcncq">G</span>
        <div className="relative" data-id="element-153" data-mp="3i859eysk4z6y">
          <div className="mx-auto mb-5 flex items-center justify-center gap-3" aria-hidden="true" data-id="element-154" data-mp="3i7vdkuktzxtc">
            <span className="h-px w-10 bg-metallic-border/65 sm:w-16" data-id="element-155" data-mp="3mbtgzhh6vus7" />
            <span className="h-2 w-2 rotate-45 bg-metallic-border" data-id="element-156" data-mp="3ra4r7tbec7ay" />
            <span className="h-px w-10 bg-metallic-border/65 sm:w-16" data-id="element-157" data-mp="32011vk610q" />
          </div>
          <p className="font-body font-semibold text-[10px] tracking-[5px] uppercase text-gold-light mb-4" data-id="element-158" data-mp="p8bx000cah">
            {t.contact.ctaEyebrow}
          </p>
          <h2 className="font-serif font-light text-warm-white text-3xl sm:text-[42px] leading-[1.2] mb-4" data-id="element-159" data-mp="9rtp9298i77y">
            {t.contact.ctaTitleTop}
            <br data-id="element-160" data-mp="dslxptaa8836" />
            <em className="italic text-gold-light" data-id="element-161" data-mp="it9wj14r5t7y">{t.contact.ctaTitleBottom}</em>
          </h2>
          <p className="font-body font-light text-cream/80 text-sm leading-[1.85] max-w-md mx-auto mb-8" data-id="element-162" data-mp="56kofk5fbvk1">
            {t.contact.ctaBody}
          </p>

          <motion.button type="button" onClick={openBooking} whileHover={{
          y: -2
        }} className="metallic-reserve-button inline-block px-9 py-4 text-white font-body font-semibold text-[10px] tracking-[4px] uppercase outline-none focus-visible:ring-2 focus-visible:ring-gold-light focus-visible:ring-offset-2 focus-visible:ring-offset-dark sm:px-11" data-id="element-163" data-mp="14pn4fnhti69">
            {t.contact.ctaButton}
          </motion.button>

          <p className="font-body text-cream/85 text-sm mt-6 tracking-[1px]" data-id="element-164" data-mp="3ee3t1y5cqfaw">
            {t.contact.writeUs}{' '}
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="font-semibold text-gold-light underline-offset-4 transition-colors hover:text-warm-white hover:underline" data-id="element-165" data-mp="3ncp8vljfvg7z">
              WhatsApp (347) 888-6047
            </a>
            {' '}{t.contact.orCall}{' '}
            <a href={`tel:${PHONE_TEL}`} className="font-semibold text-gold-light underline-offset-4 transition-colors hover:text-warm-white hover:underline" data-id="element-166" data-mp="12okpd0cvjhe7">
              (347) 242-2967
            </a>
          </p>
          <p className="font-body font-light text-cream/60 text-xs mt-2 tracking-[0.5px]" data-id="element-167" data-mp="2bjetv9llo7qx">
            {t.contact.scheduleLine}
          </p>
        </div>
      </motion.div>
    </section>;
}